import Jpaper from '@atoms/JPaper';
import { JTable } from '@atoms/JTable';
import { JTablePagination } from '@atoms/JTablePagination';
import ConsumptionTableRow from '@establishment/components/ConsumptionTableRow';
import RechargeTableRow from '@establishment/components/RechargeTableRow';
import {
  useGetEStablishmentCreditsTopUp,
  useGetEstablishmentCreditsConsumption,
} from '@establishment/queries/useGetEstablishment';
import { KEY_ORDER } from '@models/key-order';
import TableColumn from '@models/table-column';
import { Box } from '@mui/material';
import EndPoints from '@services/endpoints';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const SPENDING_TABLE_COLS: TableColumn[] = [
  {
    name: 'USER',
    orderable: true,
    sortingField: 'fullname',
  },
  {
    name: 'CREDIT',
    orderable: true,
    sortingField: 'creditConsumption_credits_amount_consumed',
  },
  {
    name: 'ACTION',
    orderable: true,
    sortingField: 'creditConsumption_consumption_action',
  },

  {
    name: 'DATE_CONSUMPTION',
    orderable: true,
    sortingField: 'creditConsumption_created_at',
  },
];
const RECHARGE_TABLE_COLS: TableColumn[] = [
  {
    name: 'DATE_RECHARGE',
    orderable: true,
    sortingField: 'createdAt',
  },
  {
    name: 'PAID_AMOUNT',
    orderable: true,
    sortingField: 'eurosAmount',
  },
  {
    name: 'TOTAL_CREDIT',
    orderable: true,
    sortingField: 'creditsAmount',
  },
  {
    name: 'REPARTITION_DATE',
    orderable: true,
    sortingField: 'creditsSharing.createdAt',
  },
  {
    name: 'CREDIT_RECEIVED',
    orderable: true,
    sortingField: 'creditsSharing.creditsAmount',
  },
];
interface EstablishmentCreditsProps {
  id: string | undefined;
}
const EstablishmentCredits = ({ id }: EstablishmentCreditsProps) => {
  const PER_PAGE = 10;
  const RECHARGE_PER_PAGE = 3;
  const [page, setPage] = useState<number>(1);
  const [pageRecharge, setPageRecharge] = useState<number>(1);
  const { t } = useTranslation();

  const ESTABLISHMENT_DEFAULT_ORDER = {
    order: KEY_ORDER.DESC,
    orderBy: 'creditConsumption_credits_amount_consumed',
  };
  const [order, setOrder] = useState<{
    order: KEY_ORDER;
    orderBy: string;
  }>(ESTABLISHMENT_DEFAULT_ORDER);
  const RECHARGE_DEFAULT_ORDER = {
    order: KEY_ORDER.DESC,
    orderBy: 'creditsAmount',
  };
  const [orderRecharge, setOrderRecharge] = useState<{
    order: KEY_ORDER;
    orderBy: string;
  }>(RECHARGE_DEFAULT_ORDER);
  const { data } = useGetEstablishmentCreditsConsumption(id, {
    page,
    limit: PER_PAGE,
    order: order.order,
    orderBy: order.orderBy,
  });
  const { data: rechargeData } = useGetEStablishmentCreditsTopUp(id, {
    page: pageRecharge,
    limit: RECHARGE_PER_PAGE,
    order: orderRecharge.order,
    orderBy: orderRecharge.orderBy,
  });
  const rows = data?.data.data;
  const rechargeDataRows = rechargeData?.data.data;
  const isNotFoundConsumptions = rows?.length === 0;
  const isNotFoundRecharges = rechargeDataRows?.length === 0;
  const pagesCountConsumption = data?.data.meta.itemCount;
  const pagesCountRecharge = rechargeData?.data.meta.itemCount;
  const meta = data?.data.meta;
  const metaRecharge = rechargeData?.data.meta;
  const handleColumnClick = (column: string) => {
    if (order.orderBy === column) {
      // If user clicks on the same column, switch the order
      setOrder((prevOrder) => ({
        order:
          prevOrder.order === KEY_ORDER.ASC ? KEY_ORDER.DESC : KEY_ORDER.ASC,
        orderBy: column,
      }));
    } else {
      // If user clicks on a different column, update order field and use default order
      setOrder({
        order: KEY_ORDER.ASC,
        orderBy: column,
      });
    }
  };
  const handleRechargeColumnClick = (column: string) => {
    if (order.orderBy === column) {
      // If user clicks on the same column, switch the order
      setOrderRecharge((prevOrder) => ({
        order:
          prevOrder.order === KEY_ORDER.ASC ? KEY_ORDER.DESC : KEY_ORDER.ASC,
        orderBy: column,
      }));
    } else {
      // If user clicks on a different column, update order field and use default order
      setOrderRecharge({
        order: KEY_ORDER.ASC,
        orderBy: column,
      });
    }
  };
  return (
    <>
      <Jpaper mb={3}>
        <JTable
          tableTitle={t('CONSUMPTION_HISTORY') ?? ''}
          isNotFound={isNotFoundConsumptions}
          columns={SPENDING_TABLE_COLS}
          notFoundLabel={t('NO_CONSUMPTION_FOUND') ?? ''}
          total={pagesCountConsumption ?? 0}
          perpage={PER_PAGE}
          nbpages={pagesCountConsumption ?? 0}
          onChangeOrder={(value) => handleColumnClick(value)}
          showDownload={true}
          linkDownload={EndPoints.establishment.exportCsvConsumptions(id)}
        >
          {rows?.map((row) => (
            <ConsumptionTableRow row={row} key={row.creditConsumption_id} />
          ))}
        </JTable>
        {meta && (
          <Box mt={2}>
            <JTablePagination
              onPageChange={(value) => {
                setPage(value);
              }}
              meta={meta}
            />
          </Box>
        )}
      </Jpaper>
      <Jpaper mb={3}>
        <JTable
          tableTitle={t('RECHARGE_HISTORY') ?? ''}
          isNotFound={isNotFoundRecharges}
          columns={RECHARGE_TABLE_COLS}
          notFoundLabel={t('NO_RECHARGE_FOUND') ?? ''}
          total={pagesCountRecharge ?? 0}
          perpage={PER_PAGE}
          nbpages={pagesCountRecharge ?? 0}
          onChangeOrder={(value) => handleRechargeColumnClick(value)}
          showDownload={true}
          linkDownload={EndPoints.establishment.exportCsvTopUp(id)}
        >
          {rechargeDataRows?.map((row) => (
            <RechargeTableRow row={row} key={row.id} />
          ))}
        </JTable>
        {metaRecharge && (
          <Box mt={2}>
            <JTablePagination
              onPageChange={(value) => {
                setPageRecharge(value);
              }}
              meta={metaRecharge}
            />
          </Box>
        )}
      </Jpaper>
    </>
  );
};
export default EstablishmentCredits;
